import i18next from 'i18next';

/**
 * The PIXI game draws the table title from the raw `name` it gets on the `getTable`
 * socket payload ("Sofia 1"), so it renders "SOFIA 1" in every language while the
 * lobby shows the translated room name ("София"). The game bundle is a prebuilt
 * artifact we don't own, so we localize the room part of the name here, on the
 * socket bridge, before the payload reaches the game.
 *
 * Same key mapping the lobby uses in PokerTable/LobbyRoom and
 * `buildWhiskeyTableLabel` in globalBroadcastMessage.
 */
const ROOM_NAME_TRANSLATION_KEYS: Record<string, string> = {
	vip: 'T_SITE_VIP',
	sofia: 'T_SITE_SOFIA',
	london: 'T_SITE_LONDON',
	paris: 'T_SITE_PARIS',
	monaco: 'T_SITE_MONACO',
	macao: 'T_SITE_MACAO',
	vegas: 'T_SITE_VEGAS',
};

/**
 * "Sofia 1" -> "София 1". Names whose first word has no T_SITE_* key (tournament
 * rooms: Bronze/Silver/Gold) and numeric-only names are returned untouched.
 */
export function localizeTableName(name: unknown): string {
	if (typeof name !== 'string' || !name.trim()) {
		return typeof name === 'string' ? name : '';
	}

	const parts = name.trim().split(/\s+/);
	const key = ROOM_NAME_TRANSLATION_KEYS[parts[0].toLowerCase()];

	if (!key) {
		return name;
	}

	const roomName = String(i18next.t(key, { defaultValue: parts[0] })).trim();

	if (!roomName) {
		return name;
	}

	parts[0] = roomName;

	return parts.join(' ');
}

/**
 * Rewrites `data.name` on a `getTable` payload so the game renders the localized
 * table title. Other events pass through unchanged; the game only reads the table
 * name for display, so nothing downstream depends on the raw value.
 */
export function localizeGameSocketPayload<T>(event: string, payload: T): T {
	if (event !== 'getTable') {
		return payload;
	}

	const envelope = payload as { data?: { name?: unknown } } | null | undefined;

	if (!envelope || typeof envelope !== 'object' || typeof envelope.data?.name !== 'string') {
		return payload;
	}

	const localized = localizeTableName(envelope.data.name);

	if (localized === envelope.data.name) {
		return payload;
	}

	return {
		...envelope,
		data: { ...envelope.data, name: localized },
	} as T;
}
